import React, {useEffect, useRef, useState} from 'react';
import {
    Button,
    ButtonBase,
    Card,
    CardActionArea,
    CardMedia,
    Grid,
    Typography,
    Tooltip, IconButton
} from "@mui/material";
import {ThemeProvider, createTheme} from '@mui/material/styles';
import {Link} from "react-router-dom";
import {Comment} from './NewPost';
import ReactQuill from 'react-quill';
import { useTranslation } from 'react-i18next';
import { getLocalizedField } from '../../../utils/localizedContent';
import axios from '../../../config/axios'

/* Icons */
import {ThumbUp} from "@mui/icons-material";
import {Bookmark} from "@mui/icons-material";
import ReportIcon from '@mui/icons-material/Report';
import {CommentOutlined} from "@mui/icons-material";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import {FaHandsClapping} from "react-icons/fa6";
import {FaFaceSurprise} from "react-icons/fa6";
import {ImConfused2} from "react-icons/im";
import {DeleteForever, Edit} from "@mui/icons-material";
import { useHistory } from "react-router-dom";

/* CSS stylesheets */
import '../../../style/homepage/homepage.scss';
import useStyles from '../../../style/homepage/homepage';
import 'react-quill/dist/quill.bubble.css';
import 'react-quill/dist/quill.snow.css';

// Use same fallback as UserProfile.js
const profilepic1 = '/blogger.jpg';

const theme = createTheme({
    typography: {
        h4: {
            fontFamily: 'Montserrat',
            color: '#000000',
            margin: '0 0 5px 0',
        },
        h6: {
            fontFamily: 'Montserrat',
            fontWeight: 'bold',
            color: '#000000',
            margin: '0 0 2px 0',
        },
        body1: {
            fontFamily: 'Montserrat'
        },
        subtitle2: {
            fontFamily: 'Montserrat',
            fontWeight: 'bold'
        },
        caption: {
            fontFamily: 'Montserrat',
            color: '#807f80'
        }
    }
});
const styles = {
    title: {
        fontFamily: 'Montserrat',
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#000000',
        margin: '0 0 5px 0',
        '&:hover': {
            cursor: 'pointer',
            textDecoration: 'underline'
        },
    },
    subtitle: {
        fontFamily: 'Montserrat',
        fontSize: '1.3rem',
        color: '#000000',
        margin: '0 0 2px 0',
    },
    name: {
        fontFamily: 'Roboto',
        fontWeight: 'bold',
    },
    certification: {
        fontFamily: 'Roboto',
    },
    notification: {
        fontFamily: 'Roboto',
        fontWeight: 'bold',
    },
    warning: {
        fontFamily: 'Roboto',
        color: '#FF0000',
    },

}

const Post = ({posts, loading, usersrolesLoading, postsDataLoading, thumbnailsLoading, tag, onPostDelete, hasMore, onLoadMore }) => {
    const classes = useStyles();
    const history = useHistory();
    const { i18n } = useTranslation();
    const { t } = useTranslation();
    const lang = i18n.language?.split('-')[0];
    const token = localStorage.getItem('AUTHORIZATION_TOKEN_BLOG');
    const reactionTypeMap = {
        'like': 'LIKE',
        'heart': 'LOVE',
        'clap': 'SUPPORT',
        'surprise': 'WOW',
        'confuse': 'FUNNY'
    };

    const backend = process.env.REACT_APP_API_ENDPOINT;

    //Post popup
    const [post, setPost] = useState(null);
    const [postModal, setPostModal] = useState(false);
    const [isTimeout, setIsTimeout] = useState(false);
    const [comments, setComments] = useState([]);
    const [postReactions, setPostReactions] = useState([]);
    const [myReactions, setMyReactions] = useState([]);
    const [commentReactions, setCommentReactions] = useState({});
    const [myCommentReactions, setMyCommentReactions] = useState({});
    const [commentProfiles, setCommentProfiles] = useState([]);
    const [reportReasons, setReportReasons] = useState([]);
    const [reportDescription, setReportDescription] = useState('');
    const [reportingComment, setReportingComment] = useState(null); // holds the comment being reported

    useEffect(() => {
        // Timeout logic for 2 minutes
        const timer = setTimeout(() => {
            if (!posts || !posts.length) {
                console.log("No posts found...")
                setIsTimeout(true);
            }
        }, 10000/2); // Time in milliseconds (10 seconds)

        return () => clearTimeout(timer); // Cleanup timeout
    }, [posts]);

    useEffect(() => {
        if (postModal && post) {
            // Record post view (tracks unique views)
            const sessionId = localStorage.getItem('sessionId') || (() => {
                const newSessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now();
                localStorage.setItem('sessionId', newSessionId);
                return newSessionId;
            })();

            axios.post(`${backend}/post/${post.id}/view`, {}, {
                headers: { 'X-Session-Id': sessionId }
            }).then(response => {
                if (response.data.isNewView) {
                    // Update the post's view count in the UI
                    post.viewcount = response.data.viewCount;
                }
            }).catch(err => console.error('Error recording view:', err));

            // Fetch comments
            axios.get(`${backend}/post/${post.id}/comment`)
                .then(response => {
                    // console.log('Raw comment data:', response.data);
                    const commentsData = Array.isArray(response.data) ? response.data : [];
                    const freshComments = commentsData.map((c) =>
                        new Comment(c.id, c.body, c.firstname, c.lastname, c.userId)
                    );
                    post.comments.setCommentArray(freshComments);
                    post.comments.setTotalComments(freshComments.length);
                    setComments([...freshComments]);

                    // Fetch reactions for each comment
                    const user = JSON.parse(localStorage.getItem('user'));
                    freshComments.forEach(comment => {
                        axios.get(`${backend}/comment-reaction/${comment.id}`)
                            .then(res => {
                                const reactions = res.data;
                                const allKeys = reactions.map(r =>
                                    Object.keys(reactionTypeMap).find(key => reactionTypeMap[key] === r.reaction_type)
                                ).filter(Boolean);

                                const myKeys = reactions
                                    .filter(r => r.userId === user.id)
                                    .map(r => Object.keys(reactionTypeMap).find(key => reactionTypeMap[key] === r.reaction_type))
                                    .filter(Boolean);

                                setCommentReactions(prev => ({ ...prev, [comment.id]: allKeys }));
                                setMyCommentReactions(prev => ({ ...prev, [comment.id]: myKeys }));
                            })
                            .catch(err => console.error(err));
                    });
                })
                .catch(err => console.error(err));

            // Fetch profiles for comment photos
            axios.get(`${backend}/profile`)
                .then(response => {
                    const profileData = response.data.profiles || response.data;
                    setCommentProfiles(Array.isArray(profileData) ? profileData : []);
                })
                .catch(err => console.error(err));

            // Fetch existing reactions for this post
            axios.get(`${backend}/post-reaction/${post.id}`)
                .then(response => {
                    const user = JSON.parse(localStorage.getItem('user'));
                    const reactions = response.data;
                    const allReactionKeys = reactions.map(r => 
                        Object.keys(reactionTypeMap).find(key => reactionTypeMap[key] === r.reaction_type)
                    ).filter(Boolean);
                    setPostReactions(allReactionKeys); // all users

                    const userReactions = reactions.filter(r => r.userId === user.id);
                    const myReactionKeys = userReactions.map(r =>
                        Object.keys(reactionTypeMap).find(key => reactionTypeMap[key] === r.reaction_type)
                    ).filter(Boolean);
                    setMyReactions(myReactionKeys);          
                })
                .catch(err => console.error(err));
        }
    }, [postModal, post]);

    const sampleBlog = {
        id: "sample-post",
        title: "Welcome to the Neuroleap Blog",
        body: "This is a sample post. You can add your research-based articles to raise public awareness.",
        thumbnail: "vividKidsLogo.svg", // Use your logo or placeholder
        fullName: "Neuroleap Admin",
        role: "Moderator",
        dateCreated: new Date().toLocaleDateString(),
        readingTime: { minutes: 1, seconds: 30 },
    };

    const handleReasonChange = (e) => {
        const { value, checked } = e.target;
        if (checked) {
            setReportReasons(prev => [...prev, value]);
        } else {
            setReportReasons(prev => prev.filter(r => r !== value));
        }
    };

    //Post interactions
    const [isBookmarked, setBookmarked] = useState(false);
    const [viewComments, setViewComments] = useState(false);
    const [reportModal, setReportModal] = useState(false);
    const [commentValue, setCommentValue] = useState('');
    const [isFollowed, setFollowed] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);

    //Report interactions
    /*const [reportViolence, setReportViolence] = useState(false);
    const [reportHarassment, setReportHarassment] = useState(false);
    const [reportOffensive, setReportOffensive] = useState(false);
    const [reportPolitical, setReportPolitical] = useState(false);
    const [reportOther, setReportOther] = useState(false);
    const [reportPost, setReportPost] = useState({
        violence: false,
        harassment: false,
        offensive: false,
        political: false,
        other: {
            checked: false,
            value: '',
        },
    })*/
    //Comment Interaction
    const [commentLike, setCommentLike] = useState(false);
    const [reportCommentModal, setReportCommentModal] = useState(false);


    //Display how many posts per number
    // let postsDisplay = 5;
    // const [state, setState] = useState(postsDisplay);
    // const handlePostsDisplay = () => {
    //     setState(state + postsDisplay);
    // }

    /*   Post Interactions    */
    // Check if current user follows the post author when modal opens
    useEffect(() => {
        if (postModal && post?.userId) {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (currentUser?.id) {
                axios.get(`/follow/${currentUser.id}/following`)
                    .then(response => {
                        const data = Array.isArray(response.data) ? response.data : [];
                        const followedIds = data.map(f => f.followed_id);
                        setFollowed(followedIds.includes(post.userId));
                    })
                    .catch(err => console.error('Error checking follow status:', err));
            }
        }
    }, [postModal, post?.userId]);

    //Follow button
    const handleFollow = async () => {
        const currentUser = JSON.parse(localStorage.getItem('user'));
        const token = localStorage.getItem('AUTHORIZATION_TOKEN_BLOG');

        if (!currentUser || !token) {
            window.location.href = '/login';
            return;
        }

        if (!post?.userId || currentUser.id === post.userId) {
            return; // Can't follow yourself
        }

        setFollowLoading(true);

        try {
            if (isFollowed) {
                // Unfollow
                await axios.delete(`/follow/${post.userId}`);
                setFollowed(false);
            } else {
                // Follow
                await axios.post(`/follow/${post.userId}`, {});
                setFollowed(true);
            }
        } catch (err) {
            console.error('Error following/unfollowing:', err.response?.data?.message || err.message);
        } finally {
            setFollowLoading(false);
        }
    }

    const handleReaction = async (post, reactionKey) => {
        const user = JSON.parse(localStorage.getItem('user'));
        const token = localStorage.getItem('AUTHORIZATION_TOKEN_BLOG');
        const reactionType = reactionTypeMap[reactionKey];
        const isActive = myReactions.includes(reactionKey);

        try {
            if (isActive) {
                await axios.delete(`${backend}/post-reaction`, {
                    params: {
                        postId: post.id,
                        loggedinuser: user.id,
                        reactionType: reactionType
                    },
                    headers: { token }
                });
                setPostReactions(prev => {
                    const idx = prev.indexOf(reactionKey);
                    return prev.filter((_, i) => i !== idx);
                });
                setMyReactions(prev => prev.filter(r => r !== reactionKey));
            } else {
                await axios.post(`${backend}/post-reaction`, {
                    reactionType: reactionType,
                    userId: user.id,
                    postId: post.id
                }, {
                    headers: { token }
                });
                setPostReactions(prev => [...prev, reactionKey]);
                setMyReactions(prev => [...prev, reactionKey]);
            }
        } catch (err) {
            console.error(`Error handling ${reactionKey} reaction:`, err);
        }
    };

    const handleCommentReaction = async (comment, reactionKey) => {
        const user = JSON.parse(localStorage.getItem('user'));
        const reactionType = reactionTypeMap[reactionKey];
        const currentMyReactions = myCommentReactions[comment.id] || [];
        const isActive = currentMyReactions.includes(reactionKey);

        try {
            if (isActive) {
                await axios.delete(`${backend}/comment-reaction`, {
                    params: {
                        commentId: comment.id,
                        loggedinuser: user.id,
                        reactionType: reactionType
                    }
                });
                setCommentReactions(prev => {
                    const updated = [...(prev[comment.id] || [])];
                    const idx = updated.indexOf(reactionKey);
                    return { ...prev, [comment.id]: updated.filter((_, i) => i !== idx) };
                });
                setMyCommentReactions(prev => ({
                    ...prev,
                    [comment.id]: (prev[comment.id] || []).filter(r => r !== reactionKey)
                }));
            } else {
                await axios.post(`${backend}/comment-reaction`, {
                    reactionType: reactionType,
                    userId: user.id,
                    commentId: comment.id
                });
                setCommentReactions(prev => ({
                    ...prev,
                    [comment.id]: [...(prev[comment.id] || []), reactionKey]
                }));
                setMyCommentReactions(prev => ({
                    ...prev,
                    [comment.id]: [...(prev[comment.id] || []), reactionKey]
                }));
            }
        } catch (err) {
            console.error(`Error handling comment ${reactionKey} reaction:`, err);
        }
    };

    // function countTotalReactions(post, reaction) {
    //     let count = 0;
    //     switch (reaction) {
    //         case 'like': {
    //             post.reactions.forEach((element) => element === 'like' && count++);
    //             break;
    //         }
    //         case 'heart': {
    //             post.reactions.forEach((element) => element === 'heart' && count++);
    //             break;
    //         }
    //         case 'clap': {
    //             post.reactions.forEach((element) => element === 'clap' && count++);
    //             break;
    //         }
    //         case 'surprise': {
    //             post.reactions.forEach((element) => element === 'surprise' && count++);
    //             break;
    //         }
    //         case 'confuse': {
    //             post.reactions.forEach((element) => element === 'confuse' && count++);
    //             break;
    //         }
    //     }
    //     return count;
    // }

    function Reactions({post, handleReaction, postReactions}) {
        const [open, setOpen] = useState(false);
        return (
            <div style={{position: 'relative', display: 'inline-block'}}>
                <Tooltip title={'Add reactions'} placement={'bottom'} arrow={true}>
                    <IconButton onClick={() => setOpen(!open)}>
                        <AddCircleIcon style={{fontSize: 28}} className={'addReactionIcon'}/>
                    </IconButton>
                </Tooltip>
                {open && (!postReactions.some(r => r === 'like') ||
                        !postReactions.some(r => r === 'heart') ||
                        !postReactions.some(r => r === 'clap') ||
                        !postReactions.some(r => r === 'surprise') ||
                        !postReactions.some(r => r === 'confuse')) &&
                    <div style={{position: 'absolute', zIndex: 100, top: '50%', left: '100%', transform: 'translateY(-50%)', marginLeft: '8px'}}>
                        <Grid container alignItems={'center'} justifyContent={'space-around'}
                              className={'reactionDropDown'}>
                            {!postReactions.some(r => r === 'like') &&
                                <Grid item>
                                    <Tooltip title={'Like'} placement={'bottom'} arrow={true}>
                                        <IconButton onClick={() => { handleReaction(post, 'like'); setOpen(false); }}>
                                            <ThumbUp style={{fontSize: 28}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!postReactions.some(r => r === 'heart') &&
                                <Grid item>
                                    <Tooltip title={'Heart'} placement={'bottom'} arrow={true}>
                                        <IconButton onClick={() => { handleReaction(post, 'heart'); setOpen(false); }}>
                                            <FavoriteIcon style={{fontSize: 28}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!postReactions.some(r => r === 'clap') &&
                                <Grid item>
                                    <Tooltip title={'Applause'} placement={'bottom'} arrow={true}>
                                        <IconButton onClick={() => { handleReaction(post, 'clap'); setOpen(false); }}>
                                            <FaHandsClapping style={{fontSize: 28}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!postReactions.some(r => r === 'surprise') &&
                                <Grid item>
                                    <Tooltip title={'Surprise'} placement={'bottom'} arrow={true}>
                                        <IconButton onClick={() => { handleReaction(post, 'surprise'); setOpen(false); }}>
                                            <FaFaceSurprise style={{fontSize: 28}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!postReactions.some(r => r === 'confuse') &&
                                <Grid item>
                                    <Tooltip title={'Don\'t understand'} placement={'bottom'} arrow={true}>
                                        <IconButton onClick={() => { handleReaction(post, 'confuse'); setOpen(false); }}>
                                            <ImConfused2 style={{fontSize: 28}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                        </Grid>
                    </div>
                }
            </div>
        )
    }

    function CommentReactions({ comment, handleCommentReaction, commentReactions }) {
        const [open, setOpen] = useState(false);
        const reactions = commentReactions[comment.id] || [];
        return (
            <div style={{position: 'relative', display: 'inline-block'}}>
                <Tooltip title={'Add reactions'} placement={'bottom'} arrow={true}>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        <AddCircleIcon style={{fontSize: 18}} className={'addReactionIcon'}/>
                    </IconButton>
                </Tooltip>
                {open && (!reactions.some(r => r === 'like') ||
                        !reactions.some(r => r === 'heart') ||
                        !reactions.some(r => r === 'clap') ||
                        !reactions.some(r => r === 'surprise') ||
                        !reactions.some(r => r === 'confuse')) &&
                    <div style={{position: 'absolute', zIndex: 100, top: '50%', left: '100%', transform: 'translateY(-50%)', marginLeft: '4px'}}>
                        <Grid container alignItems={'center'} justifyContent={'space-around'}
                            className={'reactionDropDown'} style={{padding: '0.5rem 0.75rem'}}>
                            {!reactions.some(r => r === 'like') &&
                                <Grid item>
                                    <Tooltip title={'Like'} placement={'bottom'} arrow={true}>
                                        <IconButton size="small" onClick={() => { handleCommentReaction(comment, 'like'); setOpen(false); }}>
                                            <ThumbUp style={{fontSize: 18}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!reactions.some(r => r === 'heart') &&
                                <Grid item>
                                    <Tooltip title={'Heart'} placement={'bottom'} arrow={true}>
                                        <IconButton size="small" onClick={() => { handleCommentReaction(comment, 'heart'); setOpen(false); }}>
                                            <FavoriteIcon style={{fontSize: 18}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!reactions.some(r => r === 'clap') &&
                                <Grid item>
                                    <Tooltip title={'Applause'} placement={'bottom'} arrow={true}>
                                        <IconButton size="small" onClick={() => { handleCommentReaction(comment, 'clap'); setOpen(false); }}>
                                            <FaHandsClapping style={{fontSize: 18}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!reactions.some(r => r === 'surprise') &&
                                <Grid item>
                                    <Tooltip title={'Surprise'} placement={'bottom'} arrow={true}>
                                        <IconButton size="small" onClick={() => { handleCommentReaction(comment, 'surprise'); setOpen(false); }}>
                                            <FaFaceSurprise style={{fontSize: 18}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                            {!reactions.some(r => r === 'confuse') &&
                                <Grid item>
                                    <Tooltip title={"Don't understand"} placement={'bottom'} arrow={true}>
                                        <IconButton size="small" onClick={() => { handleCommentReaction(comment, 'confuse'); setOpen(false); }}>
                                            <ImConfused2 style={{fontSize: 18}}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            }
                        </Grid>
                    </div>
                }
            </div>
        );
    }

    //Report button for Posts
    const handleReport = async (post) => {
        if (reportReasons.length === 0) {
            return;
        }
        try {
            await axios.post(`${backend}/report/post`, {
                postId: post.id,
                reportedUserId: post.userId,
                reasons: reportReasons,
                description: reportDescription,
            }, { headers: { token } });
            setReportReasons([]);
            setReportDescription('');
            toggleReportPopup();
             console.log(post);
        } catch (err) {
            console.error('Error reporting post:', err);
        }
    };

    const handleReportComment = async () => {
        if (reportReasons.length === 0) {
            return;
        }
        try {
            await axios.post(`${backend}/report/comment`, {
                commentId: reportingComment.id,
                postId: post.id,
                reportedUserId: reportingComment.userId,
                reasons: reportReasons,
                description: reportDescription,
            }, { headers: { token } });
            setReportReasons([]);
            setReportDescription('');
            setReportingComment(null);
            toggleReportCommentPopup();
        } catch (err) {
            console.error('Error reporting comment:', err);
        }
    };

    // function toggleReportPopup() {
    //     setReportModal(!reportModal);
    // }

    function toggleReportPopup() {
        setReportModal(!reportModal);
        setReportReasons([]);
        setReportDescription('');
    }

    function toggleReportCommentPopup() {
        setReportCommentModal(!reportCommentModal);
        setReportReasons([]);
        setReportDescription('');
    }

    //Commenting
    function toggleComments() {
        setViewComments(!viewComments);
    }

    async function  submitComment(post, comment) {
        //console.log("UserID:" +JSON.parse(localStorage.getItem('user')));
        const token = localStorage.getItem('AUTHORIZATION_TOKEN_BLOG');
        let user = JSON.parse(localStorage.getItem('user'));
        let postid = post.id
        const characterLimit = 1000;
        if (comment.length > characterLimit) {
            comment.slice(0, 1000);
        }

        await axios.post(`${backend}/post/comment`, {
            body: comment,
            postId: postid,
            userId: user.id,
        }, {
            headers: { token } // to prevent unauthorized error from backend
        });
        //call the backend for the new comment.
        let comments_fetched = await axios.get(`${backend}/post/${postid}/comment`)
        let data = comments_fetched.data;

        // Build fresh comment array using actual DB ids
        const freshComments = data.map((comment_fetched) =>
            new Comment(comment_fetched.id, comment_fetched.body, comment_fetched.firstname, comment_fetched.lastname, comment_fetched.userId)
        );

        // Update in-memory object
        post.comments.setCommentArray(freshComments);
        post.comments.setTotalComments(freshComments.length);

        // Tell React to re-render
        setComments([...freshComments]);

        // data.map((comment_fetched) => {
        //     post.setComments(new Comment(post.comments.commentArray.length + 1, comment_fetched.body, comment_fetched.firstname, comment_fetched.lastname));
        // })
        // post.setComments(new Comment(post.comments.commentArray.length + 1, comment));
        setCommentValue('');
        //console.log('Comment = ', comment);
        //console.log('Post = ', post);
    }

    const handleComment = (event) => {
        setCommentValue(event.target.value);
    }
    const commentCharacterLimit = (event) => {
        if (commentValue.length > 1000 && event.key !== 'Backspace') {
            event.preventDefault();
        }
    }

    async function deleteComment(post, comment) {
        const user = JSON.parse(localStorage.getItem('user'));
        const token = localStorage.getItem('AUTHORIZATION_TOKEN_BLOG');
        try {
            await axios.delete(`${backend}/post/comment/${user.id}/${comment.id}`, {
                headers: { token }
            });
            post.comments.deleteComment(comment.comment);
            setComments([...post.comments.commentArray]);
        } catch (err) {
            console.error('Error deleting comment:', err);
        }
    }

    async function deletePost(post) {
        const token = localStorage.getItem('AUTHORIZATION_TOKEN_BLOG');
        try {
            await axios.delete(`${backend}/user/post/${post.id}`, {
                headers: { token }
            });
            // tell parent to remove it from the list
            onPostDelete(post.id);
        } catch (err) {
            console.error('Error deleting post:', err);
        }
    }


    //Bookmark button
    const handleBookmark = () => {
        setBookmarked(!isBookmarked);
        if (isBookmarked === false) {
            console.log('You bookmarked this post because you liked it :)');
        }
    }

    //Popup onclick
    function togglePostPopup() {
        setPostModal(!postModal);
        setPostReactions([]); 
        setMyReactions([]);
        setCommentReactions({});
        setMyCommentReactions({});
        setViewComments(false);
        setCommentProfiles([]);

        if (postModal) {
            document.body.style.overflow = '';
        } else {
            document.body.style.overflow = 'hidden';
        }
    }

    //console.log('Displayed Posts = ', state);
    //console.log('Posts = ', posts);


    return (
        loading || usersrolesLoading || postsDataLoading || thumbnailsLoading
            ? <div className={'loadingPosts'}><Grid container>{'Loading Posts'}</Grid></div>

            : <div className={'postList'}>
                {
                    isTimeout && (!posts || !posts.length) ? (
                        // Render the sample blog if timeout occurs and no posts are found
                        <Card key={sampleBlog.id} className={classes.root} raised={true}>
                            <ThemeProvider theme={theme}>
                                <Grid container direction={'column'} spacing={2}>
                                    <Grid item container direction={"row"}>
                                        <Grid item container xs={6} className={'borderRight'}>
                                            <Grid item container direction={'column'} justifyContent={'flex-start'}
                                                  alignItems={'flex-start'} xs={12}>
                                                <Typography style={styles.title}>
                                                    {sampleBlog.title}
                                                </Typography>
                                                <Typography style={styles.subtitle}>
                                                    {"Get started by sharing your research"}
                                                </Typography>
                                            </Grid>
                                            <Grid item container direction={'row'} justifyContent={'flex-start'}
                                                  alignItems={'center'}>
                                                <Grid item container direction={'column'} alignItems={'center'}
                                                      xs={4}>
                                                    <img className={'postProfilePicture'} src={profilepic1}
                                                         alt={'Profile thumbnail'}/>
                                                </Grid>
                                                <Grid item container xs={6} direction={"column"}
                                                      justifyContent={'center'} alignItems={'flex-start'}>
                                                    <Typography style={styles.name}>
                                                        {sampleBlog.fullName}
                                                    </Typography>
                                                    <Typography style={styles.certification}>
                                                        {sampleBlog.role}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item container direction={'column'} justifyContent={'center'} xs={6}>
                                            <Grid item container justifyContent={'center'} alignItems={'center'}>
                                                <CardMedia
                                                    style={{maxHeight: '200px', width: 'auto'}}
                                                    component={'img'}
                                                    src={sampleBlog.thumbnail}
                                                    title={'Article thumbnail'}/>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item container direction={'column'} xs={12} className={'borderTop'}>
                                        <Grid item>
                                            <Typography variant={'body1'}>
                                                <ReactQuill
                                                    value={sampleBlog.body}
                                                    readOnly={true} theme={'bubble'}
                                                    className={'postQuill'}
                                                />
                                            </Typography>
                                        </Grid>
                                        <Grid item container direction={'row'}>
                                            <Grid item style={{marginRight: '10px'}}>
                                                <Typography variant="subtitle2">
                                                    {sampleBlog.readingTime.minutes} minutes to read | {sampleBlog.dateCreated}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </ThemeProvider>
                        </Card>
                    ) : (
                    posts && Array.isArray(posts) && posts.length
                        ? posts.map((post) => {
                            const localizedTitle = getLocalizedField(post, 'title', lang);
                            const localizedSubtitle = getLocalizedField(post, 'subtitle', lang);
                            const localizedBody = getLocalizedField(post, 'body', lang);
                            return (
                            <Card key={post.id} className={classes.root} raised={true}>
                                <ThemeProvider theme={theme}>
                                    {/*Grid container for post*/}
                                    <Grid container direction={'column'} spacing={2} onClick={() => {
                                        setPost(post);
                                        togglePostPopup();
                                    }}>
                                        <Grid item container direction={"row"}>
                                            <Grid item container xs={6} className={'borderRight'}>
                                                {/*Title*/}
                                                <Grid item container direction={'column'} justifyContent={'flex-start'}
                                                      alignItems={'flex-start'} xs={12}>
                                                    <Typography style={styles.title}>
                                                        {localizedTitle}
                                                    </Typography>
                                                    <Typography style={styles.subtitle}>
                                                        {localizedSubtitle}
                                                    </Typography>
                                                </Grid>
                                                {/*Grid container for profile pic/user*/}
                                                <Grid item container direction={'row'} justifyContent={'flex-start'}
                                                      alignItems={'center'}>
                                                    <Grid item container direction={'column'} alignItems={'center'}
                                                          xs={4}>
                                                        {/*Avatar/User Profile*/}
                                                        <img
                                                            className={'postProfilePicture'}
                                                            src={post.profilePhoto || profilepic1}
                                                            alt={'Profile thumbnail'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                history.push(`/profile/${post.userId}`);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                            onError={(e) => { e.target.src = profilepic1; }}
                                                        />
                                                    </Grid>
                                                    <Grid item container xs={6} direction={"column"}
                                                          justifyContent={'center'} alignItems={'flex-start'}>
                                                        {/* Username/Name */}
                                                        <Typography
                                                            style={{ ...styles.name, cursor: 'pointer' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                history.push(`/profile/${post.userId}`);
                                                            }}
                                                        >
                                                            {post.fullName}
                                                        </Typography>
                                                        {/*User's role*/}
                                                        <Typography style={styles.certification}>
                                                            {post.role}
                                                        </Typography>
                                                        {/* User's Certification */}
                                                        <Typography style={styles.certification}>
                                                            {'Occupational Therapist'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            {/* Post Cover Image */}
                                            <Grid item container direction={'column'} justifyContent={'center'} xs={6}>
                                                {/* Checks if the post has a Cover Image or not */}
                                                <Grid item container justifyContent={'center'} alignItems={'center'}>
                                                    <CardMedia
                                                        style={{maxHeight: '200px', width: 'auto'}}
                                                        component={'img'}
                                                        src={post.thumbnail ? post.thumbnail : 'vividKidsLogo.svg'}
                                                        title={'Article thumbnail'}/>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {/*Body message*/}
                                        <Grid item container direction={'column'} xs={12} className={'borderTop'}>
                                            {/* Checks if body is greater than 600 characters to show a preview */}
                                            <Grid item>
                                                <Typography variant={'body1'}>
                                                    <ReactQuill
                                                        value={String(localizedBody).length > 600
                                                            ? localizedBody.slice(0, 600).concat('.....')
                                                            : localizedBody
                                                        }
                                                        readOnly={true} theme={'bubble'}
                                                        className={'postQuill'}
                                                    />
                                                </Typography>
                                            </Grid>
                                            {/* Post's date and minutes of read */}
                                            <Grid item container direction={'row'}>
                                                <Grid item style={{marginRight: '10px'}}>
                                                    {/*Time to read*/}
                                                    <Typography variant="subtitle2">
                                                        {post.readingTime?.minutes < 1
                                                            ? post.readingTime?.seconds + ' second(s) to read'
                                                            : post.readingTime?.minutes + ' minute(s) to read'}
                                                        {' | ' + (post.viewCount || 0) + ' views'}
                                                        {' | ' + post.dateCreated}
                                                    </Typography>
                                                </Grid>
                                                {/* Date Modified */}
                                                <Grid item>
                                                    {post.edited ? <Typography
                                                        variant={'caption'}>{'(' + post.dateModified + ' modified*)'}
                                                    </Typography> : ''}
                                                </Grid>
                                                <Grid item>
                                                    {(() => {
                                                        const user = JSON.parse(localStorage.getItem('user'));
                                                        return user && post.userId === user.id;
                                                    })() &&
                                                        <>
                                                        <Tooltip title={'Edit'}
                                                                    placement={'bottom'}
                                                                    arrow={true}>
                                                            <IconButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    history.push(`/write/${post.id}`);
                                                                }}>
                                                                <Edit style={{fontSize: 20}}/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={'Delete'}
                                                                    placement={'bottom'}
                                                                    arrow={true}>
                                                            <IconButton>
                                                                <DeleteForever
                                                                    onClick={(e) => {
                                                                        e.stopPropagation(); // prevents opening post popup
                                                                        deletePost(post);
                                                                    }}
                                                                    style={{fontSize: 20}}/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        </>
                                                        }
                                                    </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </ThemeProvider>

                            </Card>
                        )})

                        : <div style={{width: '850px'}}>
                            <Grid container justifyContent={'center'} alignItems={'center'}>
                                {'No posts by that category'}
                            </Grid>
                        </div>
                    )
                }
                {
                    postModal &&
                    <div className={'postPopup'}>
                        <div className={'postPopupBody'}>
                            <button className={'popupCloseBtn'}
                                    onClick={() => {
                                        togglePostPopup()
                                        setPost(null)
                                    }}>
                                X
                            </button>
                            <ThemeProvider theme={theme}>
                                <Grid container direction={'column'} alignItems={'center'} xs={2}
                                      style={{float: 'right'}}>
                                    <Grid item>
                                        <Typography variant={'caption'}>
                                            {post.readingTime.minutes < 1
                                                ? post.readingTime.seconds + ' second(s) to read'
                                                : post.readingTime.minutes + ' minute(s) to read'
                                                + ' | ' + post.dateCreated}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant={'caption'}>
                                            {post.viewCount || 0} views
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        {post.edited ? <Typography
                                            variant={'caption'}>{'(' + post.dateModified + ' modified*)'}
                                        </Typography> : ''}
                                    </Grid>
                                </Grid>
                                <img
                                    src={post.thumbnail ? post.thumbnail : 'vividKidsLogo.svg'}
                                    alt={'Article thumbnail'} style={{paddingBottom: '1rem'}}
                                    className={'borderBottom'}/>
                                <Typography variant={'h4'}>{getLocalizedField(post, 'title', lang)}</Typography>
                                <Typography variant="h6">{getLocalizedField(post, 'subtitle', lang)}</Typography>
                                <Grid container direction={'column'} spacing={2}>
                                    <Grid item container direction={'row'} spacing={2} xs={12}>
                                        <Grid item>
                                            <img
                                                src={post.profilePhoto || profilepic1}
                                                className={'popupProfilePicture'}
                                                alt={'Profile thumbnail'}
                                                onClick={() => {
                                                    togglePostPopup();
                                                    history.push(`/profile/${post.userId}`);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                                onError={(e) => { e.target.src = profilepic1; }}
                                            />
                                        </Grid>
                                        <Grid item container direction={'column'} justifyContent={'center'} xs={3}>
                                            <Typography
                                                style={{ ...styles.name, cursor: 'pointer' }}
                                                onClick={() => {
                                                    togglePostPopup();
                                                    history.push(`/profile/${post.userId}`);
                                                }}
                                            >
                                                {'Article by: ' + post.fullName}
                                            </Typography>
                                            <Typography
                                                style={styles.certification}>{'Role: ' + post.role}
                                            </Typography>
                                            <Typography style={styles.certification}>
                                                {'Certification(s): Occupational Therapist'}
                                            </Typography>
                                        </Grid>
                                        <Grid item container alignItems={'center'} justifyContent={'center'} xs={2}>
                                            {(() => {
                                                const currentUser = JSON.parse(localStorage.getItem('user'));
                                                // Don't show follow button for your own posts
                                                if (currentUser?.id === post.userId) return null;
                                                return (
                                                    <button
                                                        className={`${isFollowed ? 'followedButton' : ''}`}
                                                        onClick={handleFollow}
                                                        disabled={followLoading}
                                                        style={{ cursor: followLoading ? 'wait' : 'pointer' }}>
                                                        {followLoading
                                                            ? <Typography>...</Typography>
                                                            : isFollowed
                                                                ? <Typography>{t('Following')}</Typography>
                                                                : <Typography>{t('Follow')}</Typography>
                                                        }
                                                    </button>
                                                );
                                            })()}
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        {token ? (
                                            <ReactQuill value={getLocalizedField(post, 'body', lang)} readOnly={true} theme={'bubble'}
                                                        className={'borderTop'}/>
                                        ) : (
                                            <>
                                                <ReactQuill
                                                    value={String(getLocalizedField(post, 'body', lang)).length > 350
                                                        ? getLocalizedField(post, 'body', lang).slice(0, 350).concat('.....')
                                                        : getLocalizedField(post, 'body', lang)
                                                    }
                                                    readOnly={true} theme={'bubble'}
                                                    className={'borderTop'}
                                                />
                                                <div style={{
                                                    textAlign: 'center',
                                                    padding: '2rem',
                                                    borderTop: '1px solid #eee',
                                                    marginTop: '1rem'
                                                }}>
                                                    <Typography variant="h6" style={{fontFamily: 'Montserrat', marginBottom: '1rem'}}>
                                                        Want to read more?
                                                    </Typography>
                                                    <Typography variant="body1" style={{fontFamily: 'Montserrat', marginBottom: '1rem'}}>
                                                        Join our community to access full articles and engage with authors.
                                                    </Typography>
                                                    <Link to={'/login'} onClick={togglePostPopup}>
                                                        <button style={{
                                                            backgroundColor: '#6a1b9a',
                                                            color: 'white',
                                                            padding: '10px 24px',
                                                            borderRadius: '20px',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontFamily: 'Montserrat',
                                                            fontSize: '1rem'
                                                        }}>
                                                            Join Community
                                                        </button>
                                                    </Link>
                                                </div>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid container direction={'row'} justifyContent={'flex-end'} alignItems={'center'}
                                      style={{marginBottom: '2rem'}}>
                                    <Grid item container justifyContent={'center'}
                                          className={'postInteractionContainer'}>
                                        {
                                            token &&
                                            <>
                                                <Grid item style={{paddingRight: '2.5rem'}}>
                                                    {/*<ThumbUp
                                                        className={`${post.liked ? 'liked' : ''}`}
                                                        onClick={() => handleLike(post)}
                                                        style={{fontSize: 50}}
                                                    />*/}
                                                    {/*<AddCircleIcon
                                                        onClick={toggleReactions}
                                                        style={{fontSize: 50}}
                                                    />*/}
                                                    <Reactions post={post} handleReaction={handleReaction} postReactions={postReactions}/>
                                                </Grid>
                                                {postReactions.some(r => r === 'like') &&
                                                    <Grid item style={{paddingRight: '2.5rem'}}>
                                                        <Tooltip title={'Like'} placement={'bottom'} arrow={true}>
                                                            <IconButton color={'inherit'}>
                                                                <Grid item container>
                                                                    <Typography style={styles.notification}>
                                                                        {postReactions.filter(r => r === 'like').length}
                                                                    </Typography>
                                                                    <ThumbUp
                                                                        className={myReactions.includes('like') ? 'liked' : ''}
                                                                        onClick={() => handleReaction(post, 'like')}
                                                                        style={{fontSize: 40}}
                                                                    />
                                                                </Grid>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                }
                                                {postReactions.some(r => r === 'heart') &&
                                                    <Grid item style={{paddingRight: '2.5rem'}}>
                                                        <Tooltip title={'Heart'} placement={'bottom'} arrow={true}>
                                                            <IconButton color={'inherit'}>
                                                                <Grid item container>
                                                                    <Typography style={styles.notification}>
                                                                        {postReactions.filter(r => r === 'heart').length}
                                                                    </Typography>
                                                                    <FavoriteIcon
                                                                        className={myReactions.includes('heart') ? 'hearted' : ''}
                                                                        onClick={() => handleReaction(post, 'heart')}
                                                                        style={{fontSize: 40}}
                                                                    />
                                                                </Grid>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                }
                                                {postReactions.some(r => r === 'clap') &&
                                                    <Grid item style={{paddingRight: '2.5rem'}}>
                                                        <Tooltip title={'Applause'} placement={'bottom'} arrow={true}>
                                                            <IconButton color={'inherit'}>
                                                                <Grid item container>
                                                                    <Typography style={styles.notification}>
                                                                        {postReactions.filter(r => r === 'clap').length}
                                                                    </Typography>
                                                                    <FaHandsClapping
                                                                        // className={'clapped'}
                                                                        className={myReactions.includes('clap') ? 'clapped' : ''}
                                                                        onClick={() => handleReaction(post, 'clap')}
                                                                        style={{fontSize: 40}}
                                                                    />
                                                                </Grid>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                }
                                                {postReactions.some(r => r === 'surprise') &&
                                                    <Grid item style={{paddingRight: '2.5rem'}}>
                                                        <Tooltip title={'Surprise'} placement={'bottom'} arrow={true}>
                                                            <IconButton color={'inherit'}>
                                                                <Grid item container>
                                                                    <Typography style={styles.notification}>
                                                                        {postReactions.filter(r => r === 'surprise').length}
                                                                    </Typography>
                                                                    <FaFaceSurprise
                                                                        className={myReactions.includes('surprise') ? 'surprised' : ''}
                                                                        onClick={() => handleReaction(post, 'surprise')}
                                                                        style={{fontSize: 40}}
                                                                    />
                                                                </Grid>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                }
                                                {postReactions.some(r => r === 'confuse') &&
                                                    <Grid item style={{paddingRight: '2.5rem'}}>
                                                        <Tooltip title={'Don\'t understand'} placement={'bottom'}
                                                                 arrow={true}>
                                                            <IconButton color={'inherit'}>
                                                                <Grid item container>
                                                                    <Typography style={styles.notification}>
                                                                        {postReactions.filter(r => r === 'confuse').length}
                                                                    </Typography>
                                                                    <ImConfused2
                                                                        className={myReactions.includes('confuse') ? 'confused' : ''}
                                                                        onClick={() => handleReaction(post, 'confuse')}
                                                                        style={{fontSize: 40}}
                                                                    />
                                                                </Grid>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                }
                                                {/*<Grid item style={{paddingRight: '2.5rem'}}>
                                                    <ThumbUp
                                                        className={`${post.liked ? 'liked' : ''}`}
                                                        onClick={() => handleLike(post)}
                                                        style={{fontSize: 50}}
                                                    />
                                                </Grid>*/}
                                                {/*<Grid item style={{paddingRight: '2.5rem'}}>
                                                    <Bookmark
                                                        className={`${isBookmarked ? 'bookmarked' : ''}`}
                                                        onClick={() => toggleBookmark()}
                                                        style={{fontSize: 50}}
                                                    />
                                                </Grid>*/}
                                                {(() => {
                                                    const user = JSON.parse(localStorage.getItem('user'));
                                                    return user && post.userId !== user.id; // only show if NOT your post
                                                })() &&
                                                <Grid item style={{paddingRight: '2.5rem'}}>
                                                    <Tooltip title={'Report'} placement={'bottom'} arrow={true}>
                                                        <IconButton color={'inherit'}>
                                                            {
                                                                post.reports.totalReports <= 0
                                                                    ?
                                                                    <ReportIcon
                                                                        className={`${post.reports.reported ? 'reported' : ''}`}
                                                                        onClick={toggleReportPopup}
                                                                        style={{fontSize: 40}}
                                                                    />
                                                                    :
                                                                    <Grid item container>
                                                                        <Typography style={styles.notification}>
                                                                            {post.reports.totalReports}
                                                                        </Typography>
                                                                        <ReportIcon
                                                                            className={`${post.reports.reported ? 'reported' : ''}`}
                                                                            onClick={toggleReportPopup}
                                                                            style={{fontSize: 40}}
                                                                        />
                                                                    </Grid>
                                                            }
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                                }
                                                {(() => {
                                                    const user = JSON.parse(localStorage.getItem('user'));
                                                    return user && post.userId === user.id; // only show if this IS your post
                                                })() &&
                                                <>
                                                    <Grid item style={{paddingRight: '2.5rem'}}>
                                                        <Tooltip title={'Edit'} placement={'bottom'} arrow={true}>
                                                            <IconButton
                                                                color={'inherit'}
                                                                onClick={() => {
                                                                    togglePostPopup();
                                                                    history.push(`/write/${post.id}`);
                                                                }}>
                                                                <Edit style={{fontSize: 40}}/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item style={{paddingRight: '2.5rem'}}>
                                                        <Tooltip title={'Delete'} placement={'bottom'} arrow={true}>
                                                            <IconButton
                                                                color={'inherit'}
                                                                onClick={() => {
                                                                    if (window.confirm('Are you sure you want to delete this post?')) {
                                                                        deletePost(post);
                                                                        togglePostPopup();
                                                                    }
                                                                }}>
                                                                <DeleteForever style={{fontSize: 40}}/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                </>
                                                }
                                            </>
                                        }
                                        <Grid item>
                                            <Tooltip title={'Comments'} placement={'bottom'} arrow={true}>
                                                <IconButton color={'inherit'}>
                                                    {
                                                        post.comments.totalComments <= 0
                                                            ? <CommentOutlined
                                                                style={{fontSize: 40}}
                                                                onClick={toggleComments}
                                                            />
                                                            :
                                                            <Grid item container>
                                                                <Typography style={styles.notification}>
                                                                    {post.comments.totalComments}
                                                                </Typography>
                                                                <CommentOutlined
                                                                    style={{fontSize: 40}}
                                                                    onClick={toggleComments}
                                                                />
                                                            </Grid>
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </ThemeProvider>

                            {/* Report Popup */}
                            {reportModal &&
                                <div className={'reportPopup'}>
                                    <div className={'reportPopupContent'}>
                                        <Grid container direction={'column'} spacing={4}>
                                            <form id={'report'}>
                                                <Grid item>
                                                    <Typography>Report?</Typography>
                                                </Grid>
                                                <Grid item container direction={'column'} style={{padding: '5px'}}>
                                                    <label htmlFor={'violence'}>
                                                        <Grid item container direction={'row'}
                                                              spacing={2}>
                                                            <Grid item>
                                                                <input type={'checkbox'}
                                                                       id={'violence'}
                                                                       name={'reason'}
                                                                       value={'violence'}
                                                                       checked={reportReasons.includes('violence')}
                                                                       onChange={handleReasonChange}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                Violence
                                                            </Grid>
                                                        </Grid>
                                                    </label>
                                                    <label htmlFor={'harassment'}>
                                                        <Grid item container direction={'row'}
                                                              spacing={2}>
                                                            <Grid item>
                                                                <input type={'checkbox'}
                                                                       id={'harassment'}
                                                                       name={'reason'}
                                                                       value={'harassment'}
                                                                       checked={reportReasons.includes('harassment')}
                                                                       onChange={handleReasonChange}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                Harassment
                                                            </Grid>
                                                        </Grid>
                                                    </label>
                                                    <label htmlFor={'offensive'}>
                                                        <Grid item container direction={'row'}
                                                              spacing={2}>
                                                            <Grid item>
                                                                <input type={'checkbox'}
                                                                       id={'offensive'}
                                                                       name={'reason'}
                                                                       value={'offensive'}
                                                                       checked={reportReasons.includes('offensive')}
                                                                       onChange={handleReasonChange}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                Offensive
                                                            </Grid>
                                                        </Grid>
                                                    </label>
                                                    <label htmlFor={'political'}>
                                                        <Grid item container direction={'row'}
                                                              spacing={2}>
                                                            <Grid item>
                                                                <input type={'checkbox'}
                                                                       id={'political'}
                                                                       name={'reason'}
                                                                       value={'political'}
                                                                       checked={reportReasons.includes('political')}
                                                                       onChange={handleReasonChange}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                Political
                                                            </Grid>
                                                        </Grid>
                                                    </label>
                                                    <label htmlFor={'offtopic'}>
                                                        <Grid item container direction={'row'}
                                                              spacing={2}>
                                                            <Grid item>
                                                                <input type={'checkbox'}
                                                                       id={'offtopic'}
                                                                       name={'reason'}
                                                                       value={'offtopic'}
                                                                       checked={reportReasons.includes('offtopic')}
                                                                       onChange={handleReasonChange}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                Off Topic
                                                            </Grid>
                                                        </Grid>
                                                    </label>
                                                    <label htmlFor={'spam'}>
                                                        <Grid item container direction={'row'}
                                                              spacing={2}>
                                                            <Grid item>
                                                                <input type={'checkbox'}
                                                                       id={'spam'}
                                                                       name={'reason'}
                                                                       value={'spam'}
                                                                       checked={reportReasons.includes('spam')}
                                                                       onChange={handleReasonChange}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                Spam
                                                            </Grid>
                                                        </Grid>
                                                    </label>
                                                    <label htmlFor={'other'}>
                                                        <Grid item container direction={'row'}
                                                              spacing={2}>
                                                            <Grid item>
                                                                <input type={'checkbox'}
                                                                       id={'other'}
                                                                       name={'reason'}
                                                                       value={'other'}
                                                                       checked={reportReasons.includes('other')}
                                                                       onChange={handleReasonChange}
                                                                />
                                                            </Grid>
                                                            <Grid item>
                                                                Other
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item>
                                                                <textarea form={'report'}
                                                                          id={'other'}
                                                                          name={'reason'}
                                                                          placeholder={'Different Reason?'}
                                                                          value={reportDescription}
                                                                          onChange={(e) => setReportDescription(e.target.value)}
                                                                />
                                                        </Grid>
                                                    </label>
                                                </Grid>
                                                <Grid item container direction={'row'}
                                                      justifyContent={'space-evenly'}
                                                      alignItems={'center'}>
                                                    <Grid item>
                                                        <button onClick={(e) => {
                                                            e.preventDefault();
                                                            toggleReportPopup();
                                                        }}>
                                                            Close
                                                        </button>
                                                    </Grid>
                                                    <Grid item>
                                                        <button onClick={(e) => {
                                                            e.preventDefault(); // prevent refreshing of the page
                                                            handleReport(post);
                                                        }}>
                                                            Report
                                                        </button>
                                                    </Grid>
                                                </Grid>
                                            </form>
                                        </Grid>
                                    </div>
                                </div>
                            }

                            {/* View Comments after clicking on icon */}
                            {viewComments &&
                                <div style={{padding: '1.5rem 0', marginTop: '1rem', borderTop: '1px solid #E0E0E0'}}>
                                    <Grid container direction={'column'} alignItems={'stretch'}
                                          spacing={2}>
                                        <Grid item>
                                            <Typography style={{
                                                fontFamily: 'Montserrat',
                                                fontSize: '1.25rem',
                                                fontWeight: 600,
                                                color: '#1C1B1F',
                                                marginBottom: '1rem'
                                            }}>
                                                Comments ({comments.length || post.comments.totalComments || 0})
                                            </Typography>
                                        </Grid>
                                        {
                                            token
                                                ?
                                                <Grid item className={'commentingContainer'}>
                                                    <textarea onChange={handleComment} value={commentValue}
                                                              className={'commentTextArea'} name={'comment'}
                                                              onKeyDown={commentCharacterLimit} id={'comment'}
                                                              rows={3} placeholder={'Write a comment...'}/>
                                                    <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', marginTop: '0.75rem'}}>
                                                        {
                                                            commentValue.length > 1000 &&
                                                            <Typography style={styles.warning}>
                                                                {'Limit 1000 characters'}
                                                            </Typography>
                                                        }
                                                        <Button onClick={() => {
                                                            submitComment(post, commentValue)
                                                        }}
                                                                disabled={commentValue.length > 1000 || commentValue.length < 1}
                                                                sx={{
                                                                    backgroundColor: '#6750A4',
                                                                    color: 'white',
                                                                    '&:hover': { backgroundColor: '#4A3880' },
                                                                    '&:disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E' }
                                                                }}
                                                                variant={'contained'}>
                                                            Submit
                                                        </Button>
                                                    </div>
                                                </Grid>
                                                :
                                                <Grid item style={{textAlign: 'center', padding: '1rem'}}>
                                                    <Link to={'/login'}>
                                                        <Typography onClick={togglePostPopup} style={{color: '#6750A4'}}>
                                                            {'Log in to comment'}
                                                        </Typography>
                                                    </Link>
                                                </Grid>
                                        }
                                        <Grid item className={'comments'}>
                                            {
                                                (comments.length > 0 ? comments : post.comments.commentArray).map((Comment) => {
                                                    return (
                                                        <React.Fragment key={Comment.id}>
                                                        <div className={'comment'}>
                                                            {/* Comment Header: Profile pic, name, role */}
                                                            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem'}}>
                                                                <img src={profilepic1} alt={'Profile thumbnail'}
                                                                     className={'commentProfilePicture'}/>
                                                                <div style={{flex: 1}}>
                                                                    <Typography style={{...styles.name, fontSize: '0.95rem', marginBottom: '2px'}}>
                                                                        {Comment._firstname + " " + Comment._lastname}
                                                                    </Typography>
                                                                    <Typography style={{...styles.certification, fontSize: '0.8rem', color: '#79747E'}}>
                                                                        User
                                                                    </Typography>
                                                                </div>
                                                            </div>

                                                            {/* Comment Body */}
                                                            <div style={{marginBottom: token ? '0.75rem' : '0'}}>
                                                                <ReactQuill value={Comment.comment} theme={'bubble'}
                                                                            className={'commentsTextArea'}
                                                                            readOnly={true}/>
                                                            </div>

                                                            {/* Comment Actions: Reactions, Report, Delete */}
                                                            {token && (
                                                                <div style={{display: 'flex', alignItems: 'center', borderTop: '1px solid #E0E0E0', paddingTop: '0.5rem', gap: '0.25rem', flexWrap: 'wrap'}}>
                                                                    <CommentReactions
                                                                        comment={Comment}
                                                                        handleCommentReaction={handleCommentReaction}
                                                                        commentReactions={commentReactions}
                                                                    />
                                                                        {(commentReactions[Comment.id] || []).some(r => r === 'like') && (
                                                                            <Tooltip title={'Like'} placement={'bottom'} arrow={true}>
                                                                                <IconButton size="small" onClick={() => handleCommentReaction(Comment, 'like')}>
                                                                                    <Typography style={{fontSize: '0.75rem', marginRight: '2px'}}>
                                                                                        {(commentReactions[Comment.id] || []).filter(r => r === 'like').length}
                                                                                    </Typography>
                                                                                    <ThumbUp
                                                                                        className={myCommentReactions[Comment.id]?.includes('like') ? 'liked' : ''}
                                                                                        style={{fontSize: 18}}
                                                                                    />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                        {(commentReactions[Comment.id] || []).some(r => r === 'heart') && (
                                                                            <Tooltip title={'Heart'} placement={'bottom'} arrow={true}>
                                                                                <IconButton size="small" onClick={() => handleCommentReaction(Comment, 'heart')}>
                                                                                    <Typography style={{fontSize: '0.75rem', marginRight: '2px'}}>
                                                                                        {(commentReactions[Comment.id] || []).filter(r => r === 'heart').length}
                                                                                    </Typography>
                                                                                    <FavoriteIcon
                                                                                        className={myCommentReactions[Comment.id]?.includes('heart') ? 'hearted' : ''}
                                                                                        style={{fontSize: 18}}
                                                                                    />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                        {(commentReactions[Comment.id] || []).some(r => r === 'clap') && (
                                                                            <Tooltip title={'Applause'} placement={'bottom'} arrow={true}>
                                                                                <IconButton size="small" onClick={() => handleCommentReaction(Comment, 'clap')}>
                                                                                    <Typography style={{fontSize: '0.75rem', marginRight: '2px'}}>
                                                                                        {(commentReactions[Comment.id] || []).filter(r => r === 'clap').length}
                                                                                    </Typography>
                                                                                    <FaHandsClapping
                                                                                        className={myCommentReactions[Comment.id]?.includes('clap') ? 'clapped' : ''}
                                                                                        style={{fontSize: 18}}
                                                                                    />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                        {(commentReactions[Comment.id] || []).some(r => r === 'surprise') && (
                                                                            <Tooltip title={'Surprise'} placement={'bottom'} arrow={true}>
                                                                                <IconButton size="small" onClick={() => handleCommentReaction(Comment, 'surprise')}>
                                                                                    <Typography style={{fontSize: '0.75rem', marginRight: '2px'}}>
                                                                                        {(commentReactions[Comment.id] || []).filter(r => r === 'surprise').length}
                                                                                    </Typography>
                                                                                    <FaFaceSurprise
                                                                                        className={myCommentReactions[Comment.id]?.includes('surprise') ? 'surprised' : ''}
                                                                                        style={{fontSize: 18}}
                                                                                    />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                        {(commentReactions[Comment.id] || []).some(r => r === 'confuse') && (
                                                                            <Tooltip title={"Don't understand"} placement={'bottom'} arrow={true}>
                                                                                <IconButton size="small" onClick={() => handleCommentReaction(Comment, 'confuse')}>
                                                                                    <Typography style={{fontSize: '0.75rem', marginRight: '2px'}}>
                                                                                        {(commentReactions[Comment.id] || []).filter(r => r === 'confuse').length}
                                                                                    </Typography>
                                                                                    <ImConfused2
                                                                                        className={myCommentReactions[Comment.id]?.includes('confuse') ? 'confused' : ''}
                                                                                        style={{fontSize: 18}}
                                                                                    />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}

                                                                        {/* Spacer */}
                                                                        <div style={{flex: 1}} />

                                                                        {/* Report button */}
                                                                        {(() => {
                                                                            const user = JSON.parse(localStorage.getItem('user'));
                                                                            return user && Comment.userId !== user.id;
                                                                        })() && (
                                                                            <Tooltip title={'Report'} placement={'bottom'} arrow={true}>
                                                                                <IconButton size="small" onClick={() => {
                                                                                    setReportingComment(Comment);
                                                                                    toggleReportCommentPopup();
                                                                                }}>
                                                                                    <ReportIcon style={{fontSize: 18}} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}

                                                                        {/* Delete button */}
                                                                        {(() => {
                                                                            const user = JSON.parse(localStorage.getItem('user'));
                                                                            return user && (Comment.userId === user.id || user.authRole === 'admin');
                                                                        })() && (
                                                                            <Tooltip title={'Delete'} placement={'bottom'} arrow={true}>
                                                                                <IconButton size="small" onClick={() => deleteComment(post, Comment)}>
                                                                                    <DeleteForever style={{fontSize: 18}} />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Report Popup */}
                                                            {reportCommentModal &&
                                                                <div className={'reportPopup'}>
                                                                    <div className={'reportPopupContent'}>
                                                                        <Grid container direction={'column'} spacing={4}>
                                                                            <form id={'report'}>
                                                                                <Grid item>
                                                                                    <Typography>Report?</Typography>
                                                                                </Grid>
                                                                                <Grid item container direction={'column'}
                                                                                    style={{padding: '5px'}}>
                                                                                    <label htmlFor={'violence'}>
                                                                                    <Grid item container direction={'row'}
                                                                                            spacing={2}>
                                                                                        <Grid item>
                                                                                            <input type={'checkbox'}
                                                                                                    id={'violence'}
                                                                                                    name={'reason'}
                                                                                                    value={'violence'}
                                                                                                    checked={reportReasons.includes('violence')}
                                                                                                    onChange={handleReasonChange}
                                                                                            />
                                                                                        </Grid>
                                                                                        <Grid item>
                                                                                            Violence
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                    </label>
                                                                                    <label htmlFor={'harassment'}>
                                                                                        <Grid item container direction={'row'}
                                                                                              spacing={2}>
                                                                                            <Grid item>
                                                                                                <input type={'checkbox'}
                                                                                                       id={'harassment'}
                                                                                                       name={'reason'}
                                                                                                       value={'harassment'}
                                                                                                       checked={reportReasons.includes('harassment')}
                                                                                                       onChange={handleReasonChange}
                                                                                                />
                                                                                            </Grid>
                                                                                            <Grid item>
                                                                                                Harassment
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    </label>
                                                                                    <label htmlFor={'offensive'}>
                                                                                        <Grid item container direction={'row'} spacing={2}>
                                                                                            <Grid item>
                                                                                                <input type={'checkbox'}
                                                                                                       id={'offensive'}
                                                                                                       name={'reason'}
                                                                                                       value={'offensive'}
                                                                                                       checked={reportReasons.includes('offensive')}
                                                                                                       onChange={handleReasonChange}
                                                                                                />
                                                                                            </Grid>
                                                                                            <Grid item>
                                                                                                Offensive
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    </label>
                                                                                    <label htmlFor={'political'}>
                                                                                        <Grid item container direction={'row'}
                                                                                              spacing={2}>
                                                                                            <Grid item>
                                                                                                <input type={'checkbox'}
                                                                                                       id={'political'}
                                                                                                       name={'reason'}
                                                                                                       value={'political'}
                                                                                                       checked={reportReasons.includes('political')}
                                                                                                       onChange={handleReasonChange}
                                                                                                />
                                                                                            </Grid>
                                                                                            <Grid item>
                                                                                                Political
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    </label>
                                                                                    <label htmlFor={'offtopic'}>
                                                                                        <Grid item container direction={'row'}
                                                                                              spacing={2}>
                                                                                            <Grid item>
                                                                                                <input type={'checkbox'}
                                                                                                       id={'offtopic'}
                                                                                                       name={'reason'}
                                                                                                       value={'offtopic'}
                                                                                                       checked={reportReasons.includes('offtopic')}
                                                                                                       onChange={handleReasonChange}
                                                                                                />
                                                                                            </Grid>
                                                                                            <Grid item>
                                                                                                Off Topic
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    </label>
                                                                                    <label htmlFor={'spam'}>
                                                                                        <Grid item container direction={'row'}
                                                                                              spacing={2}>
                                                                                            <Grid item>
                                                                                                <input type={'checkbox'}
                                                                                                       id={'spam'}
                                                                                                       name={'reason'}
                                                                                                       value={'spam'}
                                                                                                       checked={reportReasons.includes('spam')}
                                                                                                       onChange={handleReasonChange}
                                                                                                />
                                                                                            </Grid>
                                                                                            <Grid item>
                                                                                                Spam
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    </label>
                                                                                    <label htmlFor={'other'}>
                                                                                        <Grid item container direction={'row'}
                                                                                              spacing={2}>
                                                                                            <Grid item>
                                                                                                <input type={'checkbox'}
                                                                                                       id={'other'}
                                                                                                       name={'reason'}
                                                                                                       value={'other'}
                                                                                                       checked={reportReasons.includes('other')}
                                                                                                       onChange={handleReasonChange}
                                                                                                />
                                                                                            </Grid>
                                                                                            <Grid item>
                                                                                                Other
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                        <Grid item>
                                                                <textarea form={'report'}
                                                                          id={'other'}
                                                                          name={'reason'}
                                                                          placeholder={'Different Reason?'}
                                                                          value={reportDescription}
                                                                          onChange={(e) => setReportDescription(e.target.value)}
                                                                />
                                                                                        </Grid>
                                                                                    </label>
                                                                                </Grid>
                                                                                <Grid item container direction={'row'}
                                                                                      justifyContent={'space-evenly'}
                                                                                      alignItems={'center'}>
                                                                                    <Grid item>
                                                                                        <button onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            toggleReportCommentPopup();
                                                                                        }}>
                                                                                            Close
                                                                                        </button>
                                                                                    </Grid>
                                                                                    <Grid item>
                                                                                        <button onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            handleReportComment();
                                                                                        }}>
                                                                                            Report
                                                                                        </button>
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </form>
                                                                        </Grid>
                                                                    </div>
                                                                </div>
                                                            }

                                                        </React.Fragment>
                                                    )
                                                })
                                            }

                                        </Grid>
                                    </Grid>
                                </div>
                            }


                        </div>
                    </div>
                }
                {/* {state < posts.length ?
                    <Button className={'loadMoreBtn'} onClick={handlePostsDisplay}>
                        Load More
                    </Button>
                    : <div></div>
                } */}
                {hasMore &&
                    <Button className={'loadMoreBtn'} onClick={onLoadMore}>
                        {t("Load More")}
                    </Button>
                }
            </div>


        /*return (
           <Card style={{ display: 'flex', marginBottom: "2em", padding: "1em" }}>
               <Grid item container>
                   <Grid item sm container>
                       <CardContent>
                           <ThemeProvider theme={theme}>
                               <Typography variant="h3">
                                   {props.info.title}
                               </Typography>
                               <Typography variant="h4">
                                   {props.info.preview}
                               </Typography>
                           </ThemeProvider>

                           <Grid item sm container>
                               <Grid item style={{marginRight: '0.5em'}}>
                                   <ButtonBase>
                                       <Avatar variant="square" src={props.info.profilepic}/>
                                   </ButtonBase>
                               </Grid>
                               <Grid item xs container direction="column">
                                   <Grid item>
                                       <ThemeProvider theme={theme}>
                                           <Typography variant="body2" gutterBottom style={{color: '#9BC34C'}}>
                                               {props.info.author}
                                           </Typography>
                                       </ThemeProvider>
                                   </Grid>
                                   <Grid item>
                                       <ThemeProvider theme={theme}>
                                           <Typography variant="body2" gutterBottom>
                                               {props.info.date}
                                               &nbsp;&nbsp;&nbsp;
                                               {props.info.readtime + " min read"}
                                           </Typography>
                                       </ThemeProvider>
                                   </Grid>
                               </Grid>
                           </Grid>
                       </CardContent>
                   </Grid>
                   <Grid item>
                       <ButtonBase>
                           <div className='picture'>
                               <CardMedia
                                   component="img"
                                   image={props.info.pic}
                                   alt="Live from space album cover"
                               />

                           </div>
                       </ButtonBase>
                   </Grid>
               </Grid>
           </Card>
       );*/
    )
}


export default Post;
